import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Stack,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Tooltip,
  IconButton,
  FormHelperText,
} from "@financeable-com-au/financeable-ui";
import { getIsCommercialOrBusinessApplication } from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector } from "@/store/slices/applicationFormSlice";
import { type ApplicationFormSlice, type ApplicationState, InArrears } from "@/store/slices/types/applicationFormSlice";
import { DecimalMaskFormat } from "@/utils/DecimalMaskFormat";
import { defaultRepaymentFeeFrequency } from "@/utils/lenderUtils";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";

interface LoanFieldsProps {
  saveQuote: ({ actionFor, value, key }: { actionFor: string; value?: string; key?: string }) => void;
}

const LoanFields = ({ saveQuote }: LoanFieldsProps) => {
  const {
    lenderCard: { brokerage },
    quoteCalculatorFormik,
    inArrears,
    setInArrears,
  } = useLenderCardContext();
  const { applicationType } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(applicationType);
  const hasMounted = useRef(false);

  // Save quote when in arrears select field changes.
  // This behaviour is treated differently because it is an onChange event on a select field.
  // In an input field, the save is handled onBlur.
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    saveQuote({ actionFor: "inArrears", value: quoteCalculatorFormik?.values.inArrears });
  }, [quoteCalculatorFormik?.values.inArrears]);

  return (
    <Stack direction="column" rowGap={3}>
      <FormControl error={!!quoteCalculatorFormik?.errors.rate}>
        <FormLabel>Rate</FormLabel>
        <Input
          type="text"
          endDecorator="%"
          name="rate"
          value={quoteCalculatorFormik?.values.rate}
          onChange={quoteCalculatorFormik?.handleChange}
          onBlur={(e) => {
            quoteCalculatorFormik?.handleBlur(e);
            saveQuote({ actionFor: "rate" });
          }}
          slotProps={{
            input: {
              component: DecimalMaskFormat,
            },
          }}
        />
        {!!quoteCalculatorFormik?.errors.rate && (
          <FormHelperText>
            <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
            {quoteCalculatorFormik?.errors.rate}
          </FormHelperText>
        )}
      </FormControl>
      {isCommercialOrBusinessApplication && (
        <FormControl error={!!quoteCalculatorFormik?.errors.brokerage}>
          <FormLabel>
            Brokerage
            {/* Tooltip to explain the max brokerage. */}
            {!!brokerage.percentage.value && !!brokerage.max.value && (
              <Tooltip
                title={
                  <Stack padding={2} flexDirection="column" rowGap={2}>
                    <Typography>
                      There is a Maximum Brokerage of <Typography bold>{brokerage.max.formattedValue}</Typography>
                    </Typography>
                  </Stack>
                }
              >
                <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                  <InfoIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </FormLabel>
          <Input
            type="text"
            name="brokerage"
            endDecorator="%"
            value={quoteCalculatorFormik?.values.brokerage}
            onChange={quoteCalculatorFormik?.handleChange}
            onBlur={(e) => {
              quoteCalculatorFormik?.handleBlur(e);
              saveQuote({ actionFor: "brokerage" });
            }}
            slotProps={{
              input: {
                component: DecimalMaskFormat,
              },
            }}
          />
          {!!quoteCalculatorFormik?.errors.brokerage && (
            <FormHelperText>
              <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
              {quoteCalculatorFormik?.errors.brokerage}
            </FormHelperText>
          )}
        </FormControl>
      )}
      <FormControl>
        <FormLabel>In Arrears</FormLabel>
        <Select
          value={inArrears}
          name="inArrears"
          onChange={(_e, value) => {
            if (!!value) {
              const selectedInArrears = value as unknown as InArrears;
              setInArrears(selectedInArrears);
              quoteCalculatorFormik?.setFieldValue("inArrears", selectedInArrears);
            }
          }}
        >
          <Option value={InArrears.Yes}>Yes</Option>
          <Option value={InArrears.No}>No</Option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Repayment Frequency</FormLabel>
        <Select
          value={quoteCalculatorFormik?.values.repaymentFrequency}
          name="repaymentFrequency"
          onChange={(_e, value) => quoteCalculatorFormik?.setFieldValue("repaymentFrequency", value)}
        >
          {defaultRepaymentFeeFrequency.map((frequency) => (
            <Option value={frequency} key={frequency}>
              {frequency}
            </Option>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};

export { LoanFields };
